import {
  Component,
  DestroyRef,
  inject,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Self,
  signal,
  ViewEncapsulation,
  WritableSignal,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NgControl,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { NgOnInitWriteBugAbstractComponent } from '@shared/abstract/ngOnInitWriteBug.abstract.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PlIcon } from '@app/icon-library/icons';
import { IconFrame } from '@app/icon-library/lib/pl-icon.component';
import { ComponentChanges } from '@app/utils/simpleChangesType';

enum FormControlName {
  dropdown = 'dropdown',
}

interface DropdownForm {
  [FormControlName.dropdown]: FormControl<string | null>;
}

@Component({
  selector: 'pl-dropdown',
  templateUrl: './pl-dropdown.component.html',
  styleUrls: ['./pl-dropdown.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class PlDropdownComponent
  extends NgOnInitWriteBugAbstractComponent<string | null>
  implements OnInit, OnChanges, ControlValueAccessor
{
  @Input({ required: true }) public options!: string[];
  @Input() public isFormSubmitted: boolean = false;
  @Input() public isDisabled: boolean = false;
  @Input() public maxlength: number | null = 250;
  @Input() public label: string = '';
  @Input() public placeholder: string = '';

  public filteredOptions: WritableSignal<string[]> = signal([]);

  public form!: FormGroup<DropdownForm>;

  protected readonly FormControlName: typeof FormControlName = FormControlName;
  protected readonly IconFrame: typeof IconFrame = IconFrame;
  protected readonly PlIcon: typeof PlIcon = PlIcon;

  protected destroyRef: DestroyRef = inject(DestroyRef);

  constructor(@Optional() @Self() public override controlDir: NgControl) {
    super(controlDir);
    this.createForm();
  }

  private createForm(): void {
    const fb: FormBuilder = new FormBuilder();
    this.form = fb.nonNullable.group<DropdownForm>({
      [FormControlName.dropdown]: fb.control<string | null>('', []),
    });

    this.form.controls[FormControlName.dropdown].valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (value: string | null) => {
        console.log('TONIO PlDropdownComponent valuesChanges next value=', value);
        this.filterOptionsBasedOnValue(value);
        if (value !== null) {
          this.emitChanges(value);
        }
      },
    });
  }

  public plOnInit(): void {
    if (this.controlDir) {
      const control: AbstractControl | null = this.controlDir.control;

      if (control) {
        const validators: ValidatorFn[] = control.validator ? [control.validator] : [];
        control.setValidators([this.validate.bind(this, validators)]);
        control.updateValueAndValidity({ emitEvent: false });
      }
    }

    this.filteredOptions.set([...this.options]);
  }

  public ngOnChanges(changes: ComponentChanges<PlDropdownComponent>): void {
    if (changes.isDisabled) {
      if (changes.isDisabled.currentValue) {
        this.form.controls[FormControlName.dropdown].disable();
      } else {
        this.form.controls[FormControlName.dropdown].enable();
      }
    }
  }

  private validate(validators: ValidatorFn[]): ValidationErrors | null {
    const allErrors: any = {};
    const dropdownValue: string | null = this.form.controls[FormControlName.dropdown].value;
    for (const validator of validators) {
      const formControl: UntypedFormControl = new UntypedFormControl(dropdownValue);
      const hasError: ValidationErrors | null = validator(formControl);
      if (hasError) {
        Object.entries(hasError).forEach(([errorName, isOnError]: [string, boolean]) => {
          allErrors[errorName] = isOnError;
        });
      }
    }

    console.log(
      'TONIO PlDropdownComponent validate this.controlDir.value=',
      this.controlDir.value,
      '   dropdownValue=',
      dropdownValue,
      '   this.controlDir.value?.length=',
      this.controlDir.value?.length,
      '   this.maxlength=',
      this.maxlength,
    );
    if (this.maxlength !== null && dropdownValue !== null && dropdownValue.length > this.maxlength) {
      allErrors.maxlength = {
        requiredLength: this.maxlength,
        actualLength: dropdownValue.length,
      };
    }

    console.log('TONIO PlDropdownComponent validate allErrors=', allErrors);

    if (Object.keys(allErrors).length > 0) {
      return allErrors;
    }

    return null;
  }

  public plWriteValue(valueToWrite: string | null): void {
    this.form.controls[FormControlName.dropdown].setValue(valueToWrite, { emitEvent: false });
    this.filterOptionsBasedOnValue(valueToWrite);
    this.updateValueAndValidity();
  }

  private emitChanges(value: string): void {
    this.onChangeCallback(value);
    this.onTouchedCallback();
    this.updateValueAndValidity();
  }

  protected updateValueAndValidity(): void {
    if (this.controlDir.control) {
      this.controlDir.control.updateValueAndValidity({
        onlySelf: false,
        emitEvent: false,
      });
    }
  }

  public clearInputValue(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.form.controls[FormControlName.dropdown].setValue('', { emitEvent: true });
  }

  private filterOptionsBasedOnValue(value: string | null): void {
    if (value !== null) {
      const filterValue: string = value.toLowerCase();

      this.filteredOptions.set(this.options.filter((option: string) => option.toLowerCase().includes(filterValue)));
    } else {
      this.filteredOptions.set([...this.options]);
    }
  }
}
